/* hs-eslint ignored failing-rules */

/* eslint-disable prefer-promise-reject-errors */

/* eslint-disable no-bitwise */
'use es6';

import PortalIdParser from 'PortalIdParser';
import http from 'hub-http/clients/apiClient';
import Annotation from 'react-codemirror/model/Annotation';
import { once } from './miniUnderscore.js';
const EMPTY_ARRAY = Object.freeze([]);
/**
 * @param {String}    template
 * @param {Object}    options
 * @return {Object}
 */

function createRequestData(template, options) {
  const {
    isHeadHtml,
    templateType,
    isAvailableForNewContent,
    context,
    templateId,
    templatePath,
    modulePath,
    moduleContentTypes,
    moduleHostTemplateTypes
  } = options;
  const data = {
    template_source: template
  };

  if (context) {
    data.context = context;
  }

  if (isHeadHtml === !!isHeadHtml) {
    data.is_head_html = isHeadHtml;
  }

  if (isAvailableForNewContent === !!isAvailableForNewContent) {
    data.is_available_for_new_content = isAvailableForNewContent;
  }

  if (templateType === templateType >>> 0) {
    data.template_type = templateType;
  }

  if (templateId) {
    data.template_id = templateId;
  }

  if (templatePath) {
    data.template_path = templatePath;
  }

  if (modulePath) {
    data.module_path = modulePath;
  }

  if (moduleHostTemplateTypes) {
    data.host_template_types = moduleHostTemplateTypes;
  }

  if (moduleContentTypes) {
    data.content_types = moduleContentTypes;
  }

  return data;
}
/**
 * @param {Array.<Object>} errors
 * @return {Array.<Object>}
 */


function mapErrorsToAnnotations(errors) {
  if (!(errors && errors.length)) {
    return EMPTY_ARRAY;
  }

  return errors.map(Annotation.fromHublValidation);
}
/**
 * @typedef {Function} CancelableHublClient
 * @property {Function} cancel      - Cancels the promise(s).
 * @property {Function} resume      - Resumes the promise(s).
 * @property {Boolean}  isCanceled  - True if the client has been canceled, false otherwise.
 */

/**
 * Creates a client that fires callbacks only for latest request.
 * It does return a promise that can be used instead.
 * The client is cancelable - use when components unmount.
 *
 * @returns {CancelableHublClient}  HubL validation client.
 */


export function createValidateClient() {
  const uri = `/cos-rendering/v1/internal/validate?portalId=${PortalIdParser.get()}`;
  let lastValidateRequestId = 0;
  let isCanceled = false;
  /**
   * @param {String}    template      - Source code to validate.
   * @param {Object}    hublOptions   - Extra data passed to HubL validation API.
   * @param {Function}  onValidated   - (annotations, apiResponse) => {}
   * @param {Function}  onError       - (apiResponse) => {}
   * @return {Promise}
   */

  function validate(template, hublOptions, onValidated, onError) {
    const requestId = ++lastValidateRequestId;

    const isLatest = () => requestId === lastValidateRequestId;

    let postUri = uri;

    if (hublOptions && hublOptions.templatePath) {
      postUri = `${postUri}&template_path=${hublOptions.templatePath}`;
    }

    return new Promise((resolve, reject) => {
      http.post(postUri, {
        data: createRequestData(template, hublOptions || {})
      }).then(response => {
        const isLatestRequest = isLatest();

        if (isCanceled) {
          reject({
            isCanceled,
            isLatestRequest,
            response
          });
          return;
        }

        const annotations = mapErrorsToAnnotations(response.renderingErrors);
        const result = {
          annotations,
          response,
          isLatestRequest,
          isCanceled
        };

        if (onValidated && isLatestRequest) {
          onValidated(result);
        }

        resolve(result);
      }).catch(error => {
        const isLatestRequest = isLatest();
        const result = {
          error,
          isLatestRequest,
          isCanceled
        };

        if (onError && isLatestRequest && !isCanceled) {
          onError(result);
        }

        reject(result);
      });
    }).catch(result => {
      if (result.isCanceled) {// noop
      }

      if (!onError) {
        // Throw if an onError callback was not provided.
        throw result;
      }
    });
  }

  const descriptor = {
    enumerable: true
  };
  Object.defineProperties(validate, {
    cancel: Object.assign({}, descriptor, {
      value: () => {
        isCanceled = true;
      }
    }),
    resume: Object.assign({}, descriptor, {
      value: () => {
        isCanceled = false;
      }
    }),
    isCanceled: Object.assign({}, descriptor, {
      get: () => isCanceled
    })
  });
  return validate;
}
/**
 * @deprecated Use createValidateClient() to allow for parallel clients.
 */

export const validate = (() => {
  const client = createValidateClient();
  const printDeprecationWarning = once(() => {
    console.warn('react-codemirror/util/HublValidation#validate is deprecated. ' + 'Use react-codemirror/util/HublValidation#createValidateClient instead.');
  }); // Legacy params

  return (onValidated, onError, template, hublOptions) => {
    printDeprecationWarning();

    const __onValidated = onValidated && (({
      annotations,
      response
    }) => {
      return onValidated(annotations, response);
    });

    const __onError = onError && (({
      error
    }) => {
      return onError(error);
    });

    return client(template, hublOptions, __onValidated, __onError);
  };
})();